import { PhotoRecord } from "@/hooks/useUploadPhoto"

/**
 * Shared type for photo metadata embedded in create payloads.
 * Contains everything needed to reconstruct an UploadPhotoPayload
 * except for entity_id/entity_type (derived from context).
 *
 * The `file` field carries the blob in memory during the initial mutation.
 * It does not survive rehydration — after rehydration, the file is already
 * in the PowerSync attachment storage.
 */
export type PhotoInput = {
  id: string
  org_id: string
  caption?: string
  filename: string
  file_size: number
  mime_type: string
  /** The actual file blob — present during initial mutation, absent after rehydration */
  file?: File | Blob
  /**
   * Stable creation timestamp set when the photo enters the staging pipeline
   * (in stagedPhotosToInputs / usePhotoMutations.applyChanges). Used as the
   * sort key on the entity detail view, and shared by every INSERT OR REPLACE
   * of the photo's row so the value doesn't drift as the attachment queue
   * re-writes it. Falls back to `new Date().toISOString()` if absent.
   */
  created_at?: string
}

/**
 * Custom error thrown when entity creation succeeded but some photo uploads failed.
 *
 * This allows the onError handler to:
 * 1. Bridge the successfully created entity to the query cache
 * 2. Bridge succeeded photos to the photo query cache
 * 3. Spawn individual retry mutations for failed photos
 *
 * The type guard uses both `instanceof` and `error.name` to survive
 * JSON serialization during persistence/rehydration.
 */
export class PartialSuccessError extends Error {
  name = "PartialSuccessError" as const

  /** The successfully created entity data (type depends on which mutation threw) */
  entityData: unknown

  /** Photos that were uploaded successfully */
  succeededPhotos: { record: PhotoRecord; input: PhotoInput }[]

  /** Photos that failed to upload */
  failedPhotos: { input: PhotoInput; error: unknown }[]

  constructor(
    message: string,
    entityData: unknown,
    succeededPhotos: { record: PhotoRecord; input: PhotoInput }[],
    failedPhotos: { input: PhotoInput; error: unknown }[],
  ) {
    super(message)
    this.entityData = entityData
    this.succeededPhotos = succeededPhotos
    this.failedPhotos = failedPhotos
  }
}

/**
 * Type guard for PartialSuccessError.
 * Checks both instanceof and error.name to work after JSON deserialization.
 */
export function isPartialSuccessError(
  error: unknown,
): error is PartialSuccessError {
  if (error instanceof PartialSuccessError) return true
  return (
    error instanceof Error &&
    error.name === "PartialSuccessError" &&
    "entityData" in error
  )
}
